import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Receipt, CheckCheck, AlertCircle } from "lucide-react";
import { currency } from "@/lib/format";

/**
 * Bill view (dining-session "Bill" tab).
 * - Fetches itemized bill via /api/reservations/:id/bill
 * - Shows status alert based on reservation state
 * - Triggers payment flow via onPay()
 */
export default function BillView({ reservationId, reservation, canRequestBill, onPay }) {
    const [bill, setBill] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const { data } = await api.get(`/reservations/${reservationId}/bill`);
            setBill(data);
        } catch (error) {
            console.warn("[bill] fetch failed", error?.message);
        } finally {
            setLoading(false);
        }
    }, [reservationId]);

    useEffect(() => {
        load();
    }, [load]);

    if (loading) return <Skeleton className="h-80 w-full" />;
    if (!bill) return null;

    if (!bill.line_items.length) {
        return (
            <Card>
                <CardContent className="p-10 text-center">
                    <Receipt className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                    <p className="font-medium">No charges yet</p>
                    <p className="text-sm text-muted-foreground">
                        Once you place an order, your bill will appear here.
                    </p>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card>
            <CardContent className="p-6">
                <h2 className="font-display text-2xl mb-4">Bill summary</h2>
                <BillLineItems items={bill.line_items} />
                <Separator className="my-4" />
                <BillTotals bill={bill} />
                <div className="mt-6">
                    <BillFooterAction
                        reservation={reservation}
                        canRequestBill={canRequestBill}
                        total={bill.total_before_tip}
                        onPay={onPay}
                    />
                </div>
            </CardContent>
        </Card>
    );
}

function BillLineItems({ items }) {
    return (
        <div className="space-y-2">
            {items.map((li, index) => (
                <div key={`${li.name}-${index}`} className="flex items-center justify-between text-sm">
                    <div>
                        <span className="font-medium">{li.quantity}×</span> {li.name}
                        {li.status !== "served" && (
                            <Badge variant="outline" className="ml-2 text-[10px] capitalize">{li.status}</Badge>
                        )}
                    </div>
                    <div>{currency(li.line_total)}</div>
                </div>
            ))}
        </div>
    );
}

function BillTotals({ bill }) {
    return (
        <div className="space-y-1 text-sm">
            <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Subtotal</span>
                <span>{currency(bill.subtotal)}</span>
            </div>
            <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Tax ({(bill.tax_rate * 100).toFixed(1)}%)</span>
                <span>{currency(bill.tax)}</span>
            </div>
            <div className="flex items-center justify-between font-semibold pt-2">
                <span>Total before tip</span>
                <span data-testid="bill-total-before-tip">{currency(bill.total_before_tip)}</span>
            </div>
        </div>
    );
}

function BillFooterAction({ reservation, canRequestBill, total, onPay }) {
    if (reservation.status === "completed") {
        return (
            <Alert>
                <CheckCheck className="h-4 w-4" />
                <AlertTitle>Paid in full</AlertTitle>
                <AlertDescription>Receipt is available in your notifications.</AlertDescription>
            </Alert>
        );
    }
    if (canRequestBill) {
        return (
            <Button className="w-full h-11" onClick={onPay} data-testid="request-bill-pay-button">
                <Receipt className="h-4 w-4 mr-2" /> Pay now — {currency(total)}
            </Button>
        );
    }
    return (
        <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Almost there</AlertTitle>
            <AlertDescription>Bill becomes payable once all your items are served.</AlertDescription>
        </Alert>
    );
}
